/// <amd-module name="Wit/WorkItemTFVCLinks/Components/ChangesetPicker/ChangesetPickerDialog" />
import * as React from "react";
import { useCallback, useEffect, useMemo, useRef } from "react";

import { useDerivedObservable, useObservable } from "VSS/Core/Observable";
import { IPeoplePickerProvider, PeoplePickerProvider } from "VSS/Features/Identities/IdentityPickerProvider";
import { usePageContext } from "VSS/Platform/Layout";
import { Button } from "VSSUI/Button";
import { ContentSize } from "VSSUI/Callout";
import { DatePicker } from "VSSUI/DatePicker";
import { Dialog } from "VSSUI/Dialog";
import { FormItem } from "VSSUI/FormItem";
import { IIdentity, IdentityPickerDropdown } from "VSSUI/IdentityPicker";
import { ListSelection } from "VSSUI/List";
import { Observer } from "VSSUI/Observer";
import { PanelFooter } from "VSSUI/Panel";
import { TextField } from "VSSUI/TextField";
import { ISelectionRange } from "VSSUI/Utilities/Selection";

import { TfvcChangesetRef, TfvcChangesetSearchCriteria } from "VC/Common/Contracts/Git";
import { getTfvcClient } from "VC/Common/RestClient/Tfvc";

import * as Resources from "Wit/WorkItemTFVCLinks/Resources";
import { ItemPathInput } from "../ItemPathInput";
import { ChangesetsPreviewTable } from "./ChangesetPreviewTable";

interface IChangesetPickerDialogProps {
    onDismiss: () => void;
    onChangesetChosen: (changeset: TfvcChangesetRef, path: string) => void;
}

const maxChangesetsToPreview = 50;

export function ChangesetPickerDialog({ onDismiss, onChangesetChosen }: IChangesetPickerDialogProps) {
    const pageContext = usePageContext();
    const client = useMemo(() => getTfvcClient(pageContext), [pageContext]);

    const [hasSearched, setHasSearched] = useObservable(false);
    const [loadingChangesets, setLoadingChangesets] = useObservable(false);
    const [matchingChangesets, setMatchingChangesets] = useObservable<TfvcChangesetRef[]>([]);

    const selection = useRef(new ListSelection({ selectOnFocus: false, multiSelect: false }));
    const changesetPath = useRef("");

    /**
     * Enable or disable the confirm button when the selected changesets change
     */
    const [confirmDisabled, setConfirmDisabled] = useObservable(true);
    useEffect(() => {
        const onSelectionChange = (s: ISelectionRange[]) => setConfirmDisabled(s.length === 0);
        // TODO:replat this doesn't give the right result when items are deselected
        selection.current.subscribe(onSelectionChange);
        return () => selection.current.unsubscribe(onSelectionChange);
    }, []);

    /**
     * Find changesets matching the search criteria
     */
    const findChangesets = useCallback(async searchCriteria => {
        setConfirmDisabled(true);
        setHasSearched(true);
        setLoadingChangesets(true);
        const newMatchingChangesets = await client
            .getChangesets(undefined, undefined, undefined, maxChangesetsToPreview, undefined, searchCriteria)
            .catch(() => []);
        setMatchingChangesets(newMatchingChangesets);
        setLoadingChangesets(false);
        changesetPath.current = searchCriteria.itemPath;
    }, []);

    /**
     * Commit the selection
     */
    const commitSelectedChangesets = useCallback(() => {
        const selectedChangeset = matchingChangesets.value.find((_b, index) => selection.current.selected(index));
        if (!selectedChangeset) {
            return;
        }
        onChangesetChosen(selectedChangeset, changesetPath.current);
    }, []);

    return (
        <Dialog
            className="changeset-picker-dialog"
            titleProps={{ text: Resources.FindChangesets }}
            onDismiss={onDismiss}
            contentSize={ContentSize.ExtraLarge}
        >
            <ChangesetSearchCriteriaForm onSearch={findChangesets} />

            <div className="changeset-preview-table-container">
                <Observer changesets={matchingChangesets} loading={loadingChangesets} hasSearched={hasSearched}>
                    {({ changesets, loading, hasSearched }: { changesets: TfvcChangesetRef[]; loading: boolean; hasSearched: boolean }) => {
                        if (!hasSearched) {
                            return null;
                        }

                        return <ChangesetsPreviewTable loading={loading} changesets={changesets} selection={selection.current} />;
                    }}
                </Observer>
            </div>

            <Observer confirmDisabled={confirmDisabled}>
                {({ confirmDisabled }: { confirmDisabled: boolean }) => (
                    <PanelFooter
                        buttonProps={[
                            {
                                primary: true,
                                text: Resources.ConfirmChangesetSelection,
                                onClick: commitSelectedChangesets,
                                disabled: confirmDisabled
                            },
                            { text: Resources.CancelChangesetSelection, onClick: onDismiss }
                        ]}
                    />
                )}
            </Observer>
        </Dialog>
    );
}

interface IChangesetSearchCriteriaFormProps {
    onSearch: (searchCriteria: TfvcChangesetSearchCriteria) => void;
}

function ChangesetSearchCriteriaForm({ onSearch }: IChangesetSearchCriteriaFormProps) {
    const pageContext = usePageContext();
    const identityProvider = useMemo<IPeoplePickerProvider>(() => new PeoplePickerProvider(pageContext), [pageContext]);
    const [path, setPath] = useObservable("");
    const [author, setAuthor] = useObservable<IIdentity | undefined>(undefined);
    const [fromDate, setFromDate] = useObservable<Date | undefined>(undefined);
    const [toDate, setToDate] = useObservable<Date | undefined>(undefined);
    const [changesetIdText, setChangesetIdText] = useObservable<string>("");
    const changesetId = useDerivedObservable(
        changesetIdText,
        t => {
            const num = parseInt(t);
            return Number.isFinite(num) ? num : undefined;
        },
        []
    );

    const onSearchClick = () =>
        onSearch({
            author: author.value?.displayName,
            fromDate: fromDate.value?.toDateString(),
            toDate: toDate.value?.toDateString(),
            itemPath: path.value,
            fromId: changesetId.value,
            toId: changesetId.value
        } as TfvcChangesetSearchCriteria);

    return (
        <>
            <div className="margin-bottom-8">
                <Observer path={path}>{({ path }: { path: string }) => <ItemPathInput itemPath={path} onItemPathChanged={setPath} />}</Observer>
            </div>
            <div className="changeset-picker-form flex-row flex-wrap">
                <FormItem className="changeset-picker-form-item" label={Resources.AuthorLabel}>
                    <IdentityPickerDropdown pickerProvider={identityProvider} value={author} onChange={identity => setAuthor(identity)} />
                </FormItem>

                <FormItem className="changeset-picker-form-item" label={Resources.ChangesetIdLabel}>
                    <TextField inputType="number" value={changesetIdText} onChange={(e, v) => setChangesetIdText(v)} />
                </FormItem>

                <FormItem className="changeset-picker-form-item" label={Resources.FromDateLabel}>
                    <DatePicker value={fromDate} onChange={setFromDate} />
                </FormItem>

                <FormItem className="changeset-picker-form-item" label={Resources.ToDateLabel}>
                    <DatePicker value={toDate} onChange={setToDate} />
                </FormItem>

                <Button className="changeset-picker-form-item find-changesets-button" text={Resources.FindChangesets} onClick={onSearchClick} />
            </div>
        </>
    );
}
